import React, { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Loader2, X } from "lucide-react";
import { useUnifiedProductForm } from "@/hooks/useUnifiedProductForm";
import BasicInformationSection from "./sections/BasicInformationSection";
import PriceInformationSection from "./sections/PriceInformationSection";
import { useProductModal } from "@/contexts/ProductModalContext";

interface UnifiedProductFormProps {
  onClose: () => void;
}

const UnifiedProductForm: React.FC<UnifiedProductFormProps> = ({ onClose }) => {
  const { isNewProduct, setIsNewProduct } = useProductModal();
  const {
    title,
    setTitle,
    description,
    setDescription,
    amount,
    setAmount,
    billing_period,
    setBillingPeriod,
    isSubmitting,
    creatingStripeProduct,
    validationError,
    handleSubmit,
    resetForm
  } = useUnifiedProductForm(onClose);
  
  // Reset form once on mount if isNewProduct is true, then reset the flag
  useEffect(() => {
    if (isNewProduct) {
      resetForm();
      // Reset the flag immediately to avoid continuous resets
      setIsNewProduct(false);
    }
  }, [isNewProduct, resetForm, setIsNewProduct]);

  return (
    <form
      onSubmit={handleSubmit}
      className="flex flex-col h-full max-h-[100vh]"
    >
      <div className="flex-1 overflow-y-auto px-6 py-4">
        <div className="flex items-start justify-between mb-4">
          <h2 className="text-3xl font-semibold">Create New Product</h2>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          Create a one-time product or subscription with a sharable payment link
        </p>
        <Card className="p-6 mb-6 bg-[#F2F2F2]">
          <div className="space-y-8">
            <BasicInformationSection
              title={title}
              setTitle={setTitle}
              description={description}
              setDescription={setDescription}
            />
          </div>
        </Card>
        <Card className="p-6 mb-6 bg-[#F2F2F2]">
          <PriceInformationSection
            amount={amount}
            setAmount={setAmount}
            billing_period={billing_period}
            setBillingPeriod={setBillingPeriod}
            validationError={validationError}
          />
        </Card>
      </div>
      <div className="sticky bottom-0 z-10 px-6 py-4 bg-[#F9F9F9]">
        <div className="flex flex-col items-center">
          <Button
            type="submit"
            className="gradient-bg w-full rounded-md"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {creatingStripeProduct
                  ? "Creating Stripe Product..."
                  : "Publishing..."}
              </>
            ) : (
              "Publish Product"
            )}
          </Button>
        </div>
      </div>
    </form>
  );
};

export default UnifiedProductForm;
